
import React, { useEffect, useState, useCallback, useMemo } from "react";
import {
  Grid,
  Column,
  FormGroup,
  Tile,
  Stack,
  ToastNotification,
  RadioButtonGroup,
  RadioButton,
  Checkbox,
  Dropdown,
} from "@carbon/react";

const Amenities = ({
  propertyUuid = "",
  data = {},
  attributeValues = [],
  formMode = "add",
  currentStep = 4,
  lastSavedStep = 0,
  onStepChange,
  createProperty,
  saveSectionAttributes,
  onSaveSuccess,
  onComplete,
  onSaved,
  onBack,
  CommonButtons,
  onNavigateNext,
}) => {
  
  // DEBUG: Log received props
  
  console.log('🔍 Amenities (Step 4) received:', {
    formMode,
    currentStep,
    lastSavedStep,
    isEditForm: formMode === "edit" || lastSavedStep >= currentStep,
    hasPropertyUuid: !!propertyUuid,
    hasData: !!data && Object.keys(data).length > 0,
    hasAmenities: !!data?.amenities,
    amenitiesData: data?.amenities
  });

  // CHILD EDIT MODE CALCULATION
  const isEditForm = useMemo(() => {
    return formMode === "edit" || lastSavedStep >= currentStep;
  }, [formMode, lastSavedStep, currentStep]);

  // FIELD MAP
  const FIELD_MAP = useMemo(() => ({
    pgLaundry: "pg_laundry",
    pgRoomCleaning: "pg_room_cleaning",
    pgWardenFacility: "pg_warden_facility",
    pgAvailableAmenities: "pg_available_amenities",
    pgParkingType: "pg_parking_type",
  }), []);

  // STATE
  const [form, setForm] = useState({
    pgLaundry: "",
    pgRoomCleaning: "",
    pgWardenFacility: "",
    pgAvailableAmenities: [],
    pgParkingType: "",
  });

  const [, setTouched] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // FIELD UPDATES
  const updateField = useCallback((key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setTouched((prev) => ({ ...prev, [key]: true }));
  }, []);

  // API OPTIONS ONLY
  const getOptions = useCallback(
    (fieldKey) => {
      const apiKey = FIELD_MAP[fieldKey];
      if (!apiKey) return [];

      return attributeValues
        .filter((a) => a.attrName === apiKey)
        .map((a) => ({
          id: a.id,
          label: a.attr_val_name || a.label || a.value,
        }));
    },
    [attributeValues, FIELD_MAP]
  );

  // LOAD INITIAL DATA 
  useEffect(() => {
    if (!data?.amenities) {
      console.log('💧 No amenities data found');
      return;
    }

    console.log('💧 Amenities hydrating form with data:', data.amenities);

    const incoming = {};

    Object.entries(FIELD_MAP).forEach(([field, apiKey]) => {
      const raw = data.amenities[apiKey];
      if (raw == null) {
        console.log(`  - ${field}: No data for ${apiKey}`);
        return;
      }

      console.log(`  - ${field}: raw value =`, raw, 'type:', typeof raw);

      if (field === "pgAvailableAmenities") {
        // Handle array of amenities
        if (Array.isArray(raw)) {
          incoming[field] = raw.map((x) => String(x));
        } else if (typeof raw === 'string') {
          // If it's a comma-separated string
          incoming[field] = raw.split(',').map(x => String(x.trim())).filter(x => x);
        } else {
          incoming[field] = [String(raw)];
        }
      } else if (field === "pgLaundry" || field === "pgRoomCleaning" || field === "pgWardenFacility") {
        // Handle boolean fields
        if (raw === true || raw === 1 || raw === "true" || raw === "1" || raw === "yes") {
          incoming[field] = "yes";
        } else if (raw === false || raw === 0 || raw === "false" || raw === "0" || raw === "no") {
          incoming[field] = "no";
        } else {
          incoming[field] = String(raw);
        }
      } else {
        incoming[field] = String(raw);
      }
    });

    console.log('💧 Amenities incoming form data:', incoming);
    setForm((prev) => {
      const newForm = { ...prev, ...incoming };
      console.log('💧 Amenities new form state:', newForm);
      return newForm;
    });
  }, [data, FIELD_MAP]);

  // AMENITY SELECTION HANDLERS
 
  const handleAmenityChange = useCallback((amenityId, isChecked) => {
    setForm((prev) => {
      const selected = [...prev.pgAvailableAmenities];
      
      if (isChecked) {
        // Add if not already present
        if (!selected.some(id => String(id) === String(amenityId))) {
          selected.push(String(amenityId));
        }
      } else {
        // Remove if present
        const index = selected.findIndex(id => String(id) === String(amenityId));
        if (index > -1) {
          selected.splice(index, 1);
        }
      }
      
      return { ...prev, pgAvailableAmenities: selected };
    });
    setTouched((prev) => ({ ...prev, pgAvailableAmenities: true }));
  }, []);

  const isAmenitySelected = useCallback(
    (amenityId) => {
      return form.pgAvailableAmenities.some((id) => String(id) === String(amenityId));
    },
    [form.pgAvailableAmenities]
  );
  // SAVE - 
  const handleSave = useCallback(async (navigateNext = false) => {
    console.log('💾 Amenities handleSave:', { 
      navigateNext, 
      isEditForm, 
      currentStep,
      hasPropertyUuid: !!propertyUuid,
      formData: form 
    });

    setTouched(Object.keys(form).reduce((acc, f) => ({ ...acc, [f]: true }), {}));

    try {
      setSaving(true);
      setError("");
      setSuccess("");

      let uuid = propertyUuid;

      // Create property if it doesn't exist
      if (!uuid && createProperty) {
        console.log('🆕 Creating property from Step 4...');
        uuid = await createProperty();
        onSaveSuccess?.(uuid, form, currentStep);
      }

      const payload = {};

      for (const [field, apiKey] of Object.entries(FIELD_MAP)) {
        const v = form[field];
        if (v == null || v === "") continue;

        if (field === "pgAvailableAmenities") {
          if (Array.isArray(v) && v.length > 0) {
            payload[apiKey] = v.map(id => Number(id));
          }
        } else if (field === "pgLaundry" || field === "pgRoomCleaning" || field === "pgWardenFacility") {
          // Convert "yes"/"no" to boolean
          payload[apiKey] = v === "yes";
        } else if (field === "pgParkingType") {
          // Convert to number
          payload[apiKey] = Number(v);
        } else {
          payload[apiKey] = v;
        }
      }

      console.log('💾 Saving amenities:', payload);
      await saveSectionAttributes(uuid, payload, form, "amenities");

      setSuccess("Amenities saved successfully!");
      
      // Call parent's onSaveSuccess with step number
      onSaveSuccess?.(uuid, form, currentStep);
      
      // Call onComplete if provided
      onComplete?.(form);

      // Navigate to next step if requested
      if (navigateNext) {
        console.log('➡️ Navigating to next step after save');
        onNavigateNext?.(form);
      }
    } catch (err) {
      console.error('❌ Save failed:', err);
      setError(err.message || "Failed to save amenities");
    } finally {
      setSaving(false);
    }
  }, [
    form,
    isEditForm,
    currentStep,
    propertyUuid,
    createProperty,
    onSaveSuccess,
    FIELD_MAP,
    saveSectionAttributes,
    onComplete,
    onNavigateNext
  ]);

  // BUTTON HANDLERS 
  const handleSaveOnly = useCallback((e) => {
    e?.preventDefault();
    handleSave(false);
  }, [handleSave]);

  const handleSaveAndNext = useCallback((e) => {
  //  e?.preventDefault();
    handleSave(true);
  }, [handleSave]);

  // UI
  const serviceOptions = useMemo(() => [
    { id: "yes", label: "Yes" },
    { id: "no", label: "No" },
  ], []);

  const amenities = getOptions("pgAvailableAmenities");
  const parkingOptions = getOptions("pgParkingType");

  return (
    <div style={{ background: "#fff", minHeight: "100vh" }}>
      {error && (
        <ToastNotification
          kind="error"
          title="Error"
          subtitle={error}
          timeout={4000}
          onClose={() => setError("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      {success && (
        <ToastNotification
          kind="success"
          title="Success"
          subtitle={success}
          lowContrast
          timeout={3000}
          onClose={() => setSuccess("")}
          style={{ position: "fixed", top: "1rem", right: "1rem", zIndex: 9999 }}
        />
      )}

      <Grid>
        <Column lg={16}>
          <Tile style={{ padding: "2rem", backgroundColor: "white" }}>
<div style={{ textAlign: "center", marginBottom: "1rem" }}>

<p
    style={{
      color: "#0f62fe",
      fontSize: "0.95rem",
      fontWeight: 500,
      maxWidth: "700px",
      margin: "0 auto",
      lineHeight: "1.5",
    }}
  >
    Provide additional details about your place to help tenants better understand your property.
  </p>
</div>            <Stack gap={6}>
              {/* AVAILABLE SERVICES */}
              <FormGroup legendText="Available Services">
                <Grid>
                  {/* Laundry */}
                  <Column lg={5}>
                    <FormGroup legendText="Laundry">
                      <RadioButtonGroup
                        name="pgLaundry"
                        valueSelected={form.pgLaundry}
                        onChange={(value) => updateField("pgLaundry", value)}
                        orientation="vertical"
                      >
                        {serviceOptions.map((option) => (
                          <RadioButton 
                            key={option.id} 
                            id={`pgLaundry-${option.id}`} 
                            labelText={option.label} 
                            value={option.id} 
                          />
                        ))}
                      </RadioButtonGroup>
                    </FormGroup>
                  </Column>

                  {/* Room Cleaning */}
                  <Column lg={5}>
                    <FormGroup legendText="Room Cleaning">
                      <RadioButtonGroup
                        name="pgRoomCleaning"
                        valueSelected={form.pgRoomCleaning}
                        onChange={(value) => updateField("pgRoomCleaning", value)}
                        orientation="vertical"
                      >
                        {serviceOptions.map((option) => (
                          <RadioButton 
                            key={option.id} 
                            id={`pgRoomCleaning-${option.id}`} 
                            labelText={option.label} 
                            value={option.id} 
                          />
                        ))}
                      </RadioButtonGroup>
                    </FormGroup>
                  </Column>

                  {/* Warden Facility */}
                  <Column lg={5}>
                    <FormGroup legendText="Warden Facility">
                      <RadioButtonGroup
                        name="pgWardenFacility"
                        valueSelected={form.pgWardenFacility}
                        onChange={(value) => updateField("pgWardenFacility", value)}
                        orientation="vertical"
                      >
                        {serviceOptions.map((option) => (
                          <RadioButton 
                            key={option.id} 
                            id={`pgWardenFacility-${option.id}`} 
                            labelText={option.label} 
                            value={option.id} 
                          />
                        ))}
                      </RadioButtonGroup>
                    </FormGroup>
                  </Column>
                </Grid>
              </FormGroup>
              
              {/* AVAILABLE AMENITIES */}
              <FormGroup legendText="Available Amenities">
                <Grid>
                  {amenities.map((amenity) => (
                    <Column key={amenity.id} sm={4} md={4} lg={4}>
                      <Checkbox
                        id={`amenity-${amenity.id}`}
                        labelText={amenity.label}
                        checked={isAmenitySelected(amenity.id)}
                        onChange={(_, { checked }) => handleAmenityChange(amenity.id, checked)}
                      />
                    </Column>
                  ))}
                </Grid>
                {amenities.length === 0 && (
                  <p style={{ color: "#666", fontStyle: "italic", marginTop: "1rem" }}>
                    No amenities found in the system.
                  </p>
                )}
              </FormGroup>

              {/* PARKING TYPE */}
              <FormGroup>
                <Grid>
                  <Column lg={8}>
                    <Dropdown
                      id="pgParkingType"
                      titleText="Parking Type"
                      label="Select Parking Type"
                      items={parkingOptions}
                      itemToString={(item) => item?.label ?? ""}
                      selectedItem={
                        parkingOptions.find((i) => String(i.id) === String(form.pgParkingType)) || null
                      }
                      onChange={({ selectedItem }) => 
                        updateField("pgParkingType", selectedItem?.id ?? "")
                      }
                    />
                  </Column>
                </Grid>
              </FormGroup>

              {/* BUTTONS */}
              {CommonButtons && (
                <CommonButtons
                  onSave={handleSaveOnly}
                  onNext={handleSaveAndNext}
                  showSave
                  showNext
                  isSaveLoading={saving}
                  stepData={form}
                />
              )}
            </Stack>
          </Tile>
        </Column>
      </Grid>
    </div>
  );
};

export default Amenities;